import React from 'react'
import { useState, useEffect, useCallback, useRef } from 'react'
import InputGroup from '../../elements/InputGroup/input-group'
import {
  calculateTotalInterestAndPeriod,
  calculateMinimumRepayment,
} from '../../../../helpers/mortgages-calculations'
import { CURRENCY_SYMBOL } from '../../constants'
import FormatCurrency from '../../utils/currency-formatter'

interface Calculations {
  interest?: string | number
  period?: string | number
  total?: string | number
  error?: any
}

const debounce = (fn: (...args: any[]) => void, delay: number): ((...args: any[]) => void) => {
  let timeout: number | undefined
  return (...args: any[]) => {
    window.clearTimeout(timeout)
    timeout = window.setTimeout(() => fn(...args), delay)
  }
}

const CreditCardCalculator: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [calculations, setCalculations] = useState<Calculations>({
    interest: '0',
    total: '0',
    period: '0',
    error: '',
  })

  const sliderRef = useRef<HTMLInputElement | null>(null)

  const [data, setData] = useState({
    balance: '3500',
    apr: '18.9',
    amount: '95',
    errorMessage: '',
  })

  const returnData = (key: string, returnValue: string) => {
    setData((prevData) => ({
      ...prevData,
      [key]: returnValue,
    }))
  }

  const mortgageInputChange = (e: string, value: string) => returnData(e, value)

  const handleSliderChange = (e: any) => {
    const { name, value } = e.target
    mortgageInputChange(name, value)
  }

  const debounceSlider = useCallback(
    debounce((param: any) => {
      const result: Calculations = calculateTotalInterestAndPeriod(
        parseInt(param.balance),
        parseFloat(param.apr),
        parseInt(param.amount),
      )

      if (result.error) {
        const errorFun = calculateMinimumRepayment(
          parseInt(param.balance),
          parseFloat(param.apr),
          1,
          5,
        )

        setErrorMessage(`Please ensure monthly payments are more than £${errorFun}.`)
        if (sliderRef?.current) {
          sliderRef.current.max = String(parseInt((errorFun + 4).toString()))
        }
        return
      }

      setCalculations(result)
      setErrorMessage('')
    }, 400),
    [],
  )

  useEffect(() => {
    if (!data) return
    // here add calculation call and debounce it
    debounceSlider(data)
  }, [data, debounceSlider])

  return (
    <div className='CreditCardCalculator' style={{ margin: '2.5em 0' }}>
      <div className='CreditCardCalculator__headingBox'>
        <h1>Credit Card Interest Calculator</h1>
        <p>
          Our credit card interest calculator shows you the total cost of your credit card and how
          long it will take to pay off.
        </p>
      </div>
      <div className='CreditCardCalculator__box'>
        <div className='CreditCardCalculator__box--input'>
          <InputGroup
            labelText={'Card balance:'}
            prefix={'£'}
            inputName={'balance'}
            bindValue={data.balance}
            minValue={0}
            returnInputValue={mortgageInputChange}
          />
        </div>
        <div className='CreditCardCalculator__box--input'>
          <InputGroup
            labelText={'Card APR:'}
            inputName={'apr'}
            suffix={'%'}
            bindValue={data.apr}
            minValue={0}
            maxValue={40}
            returnInputValue={mortgageInputChange}
          />
        </div>
        <div className='CreditCardCalculator__box--inputSlider'>
          <InputGroup
            labelText={'Monthly repayment:'}
            prefix={'£'}
            inputName={'amount'}
            bindValue={data.amount}
            minValue={0}
            returnInputValue={mortgageInputChange}
            responseError={errorMessage}
          />
          <div className='CreditCardCalculator__box--inputSliderText'>
            slide to adjust the amount
          </div>
          <input
            className='RangeSlider'
            name='amount'
            type='range'
            ref={sliderRef}
            onChange={handleSliderChange}
            value={data.amount}
            step='5'
            min='86'
            max='1000'
          />
        </div>
        <div className='CreditCardCalculator__box--summary'>
          <div className='CreditCardCalculator__box--summaryText'>
            <strong>
              £
              {calculations && calculations.total && Number(calculations.total) !== 0
                ? FormatCurrency(calculations.total, 'GBP', CURRENCY_SYMBOL)
                : '0'}
            </strong>
            <div>total repayable incl. interest</div>
          </div>
          <div className='CreditCardCalculator__box--summaryText'>
            <strong>{calculations.period}</strong>
            <span> months</span>
            <div>until card balance paid off</div>
          </div>
        </div>
      </div>

      <div className='CreditCardCalculator__box'>
        <p className='CreditCardCalculator__box--textSmall'>
          To get started enter the balance and APR of your credit card, then adjust your repayments.
          The more you pay a month the quicker you can pay off your card and the less you&apos;ll
          pay overall.
        </p>
        <p className='CreditCardCalculator__box--textSmall'>
          Can&apos;t afford to pay more? Transfering your debt onto a 0% balance transfer card could
          save you money:
        </p>
        <a
          className='CreditCardCalculator__box--buttonSeeBalance'
          href='https://www.uswitch.com/credit-cards/credit-card-balance-transfers/?ref=repayment-calculator'
        >
          See balance transfer cards
        </a>
      </div>
    </div>
  )
}

export default CreditCardCalculator
